/**
 * ReportImageDialog — one-tap reporter for suspicious vendor gallery
 * photos. Sends `POST /api/reports/gallery-image` with a short reason
 * plus the offending image URL and vendor_id. Backend then notifies
 * every superadmin so they can review immediately.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Flag, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const REASON_PRESETS = [
  "Adult / pornographic content",
  "Abusive or hateful content",
  "Illegal substance or weapon",
  "Copyright / spam / scam",
  "Other (explain below)",
];

export default function ReportImageDialog({
  open,
  onOpenChange,
  vendorId,
  vendorName,
  imageUrl,
}) {
  const [category, setCategory] = useState(REASON_PRESETS[0]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    try {
      setBusy(true);
      await api.post("/reports/gallery-image", {
        vendor_id: vendorId,
        image_url: imageUrl,
        category,
        notes: notes.trim() || null,
      });
      toast.success("Reported — superadmins have been notified.");
      setNotes("");
      setCategory(REASON_PRESETS[0]);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent data-testid="report-image-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-destructive" /> Report this image
          </DialogTitle>
          <DialogDescription>
            {vendorName ? <>Flag the photo from <strong>{vendorName}</strong> for admin review.</> : "Flag this photo for admin review."}
          </DialogDescription>
        </DialogHeader>

        {imageUrl && (
          <div className="rounded-lg overflow-hidden border border-border/60 bg-muted">
            <img
              src={imageUrl}
              alt=""
              className="w-full max-h-40 object-contain"
            />
          </div>
        )}

        <div className="space-y-2">
          <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
            What's wrong with this image?
          </label>
          <div className="grid gap-1.5">
            {REASON_PRESETS.map((r) => (
              <label
                key={r}
                className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-muted/60"
              >
                <input
                  type="radio"
                  name="report-category"
                  value={r}
                  checked={category === r}
                  onChange={() => setCategory(r)}
                  data-testid={`report-cat-${r.split(" ")[0].toLowerCase()}`}
                  className="accent-primary"
                />
                <span>{r}</span>
              </label>
            ))}
          </div>
          <Textarea
            data-testid="report-image-notes"
            placeholder="Optional — add extra context so admins can act faster"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            disabled={busy}
          />
          <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/40 flex gap-2 text-[11px] text-muted-foreground">
            <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <span>Repeated bad-faith reports may result in your account being reviewed.</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="report-image-cancel">
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="report-image-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Flag className="h-4 w-4 mr-1" />}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
